import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  Paper,
  Tooltip,
  Box,
  Chip,
  Typography,
  IconButton,
  Divider,
} from "@mui/material";
import Pagination from "@mui/lab/Pagination";
import * as actions from "../Releases/actions";
import { Music, User, Mail, Calendar, Eye, ExternalLink, FileText, Languages, Hash, Download, Copy, ClipboardCheck } from 'lucide-react';
import _ from "lodash";
import { toastError, toastSuccess, copyToClipboard } from "../../utils";
import CustomShowModal from "../Common/customShowModal";
import SearchInput from '../../components/SearchInput';
import StatusBadge from '../../components/StatusBadge';
import EmptyState from '../../components/EmptyState';
import ActionButton from '../../components/ActionButton';

function CopyMeta({ icon, label, value }) {
  const [copied, setCopied] = useState(false);
  if (!value || value === '-') {
    return (
      <Tooltip title={label} arrow placement="top">
        <span className="approved-release-card__meta-item">
          {icon} -
        </span>
      </Tooltip>
    );
  }
  return (
    <Tooltip title={copied ? "Copied!" : `${label} — click to copy`} arrow placement="top">
      <span
        className="approved-release-card__meta-item approved-release-card__meta-item--copyable"
        onClick={() => { copyToClipboard(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      >
        {icon} {value}
        {copied ? <ClipboardCheck size={11} className="copy-tick" /> : <Copy size={11} className="copy-icon" />}
      </span>
    </Tooltip>
  );
}

export default function Lyrics(props) {
  const [lyricsNew, setLyricsNew] = useState([]);
  const [lyricsOld, setLyricsOld] = useState([]);
  const [actionsModal, setActionsModal] = useState(false);
  const [detailsData, setDetailsData] = useState();
  const [viewDetailsModal, setViewDetailsModal] = useState(false);
  const [actionId, setActionId] = useState();
  const [count, setCount] = useState(0);

  const [filters, setFilters] = useState({
    limit: 50,
    offset: 0,
    page: 1,
    search: "",
  });

  const delaySearch = useCallback(
    _.debounce((filters) => onGetLyricsOld(filters), 500),
    []
  );

  useEffect(() => {
    onGetLyricsOld(filters);
    setTimeout(() => {
      props.setLoader(false);
    }, 5000);
  }, []);

  const onGetLyricsOld = (filters) => {
    props.setLoader(true);
    actions
      .getLyricsOldAdmin({ ...filters })
      .then((res) => {
        props.setLoader(false);
        setLyricsOld(res.body);
        setCount(res.total);
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err?.message || 'Operation failed');
      });
  };

  const onPageChange = (event, page) => {
    const offset = (page - 1) * filters.limit;
    const newFilters = { ...filters, page, offset };
    setFilters(newFilters);
    onGetLyricsOld(newFilters);
  };

  const onChangeSearch = (e) => {
    const newFilters = { ...filters, search: e.target.value };
    setFilters(newFilters);
    delaySearch(newFilters);
  };

  const onGetLyricsNew = () => {
    props.setLoader(true);
    actions
      .getLyricsDestributionRequestAdmin()
      .then((res) => {
        setLyricsNew(res.body);
        props.setLoader(false);
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err?.message || 'Failed to load lyrics distribution requests');
      });
  };

  useEffect(() => {
    onGetLyricsNew();
  }, []);

  let totalPages = Math.ceil(Number(count) / filters.limit);

  const handleViewDetails = (data) => {
    setDetailsData(data);
    setViewDetailsModal(true);
  };

  const handleActionOnClick = (item) => {
    setActionsModal(true);
    setActionId(item);
  };

  const onConfirmAcceptModal = (status) => {
    props.setLoader(true);
    actions
      .updateLyricsStatusAdmin({ status: status, lyrics_id: actionId })
      .then((res) => {
        setActionsModal(false);
        onGetLyricsNew();
        onGetLyricsOld(filters);
        toastSuccess(res.message || 'Lyrics status updated successfully');
        props.setLoader(false);
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err?.message || 'Failed to update lyrics status');
      });
  };

  const renderCard = (item, isNew) => (
    <Paper
      key={item.lyrics_id}
      className="approved-release-card"
      style={{ cursor: 'default' }}
    >
      <div className="approved-release-card__artwork">
        <div className="approved-release-card__artwork-fallback">
          <FileText size={20} color="#64748B" />
          <span>Lyrics</span>
        </div>
      </div>

      <div className="approved-release-card__body">
        <div className="approved-release-card__top">
          <div className="approved-release-card__title-row">
            <Typography className="approved-release-card__title">
              {item.release_title || 'Untitled'}
            </Typography>
          </div>
          <div className="approved-release-card__status" style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            <ActionButton variant="info" sx={{ py: 0.2, px: 1.5, fontSize: '0.75rem', whiteSpace: 'nowrap' }} onClick={() => handleViewDetails(item)}>
              View Details
            </ActionButton>
            <Box sx={{ minWidth: 80, display: 'flex', justifyContent: 'center' }}>
              {isNew ? (
                <Box display="flex" gap={0.75}>
                  <ActionButton variant="success" sx={{ py: 0.2, px: 1.5, fontSize: '0.75rem' }} onClick={() => handleActionOnClick(item.lyrics_id)}>
                    Accept
                  </ActionButton>
                  <ActionButton variant="danger" sx={{ py: 0.2, px: 1.5, fontSize: '0.75rem' }} onClick={() => handleActionOnClick(item.lyrics_id)}>
                    Reject
                  </ActionButton>
                </Box>
              ) : (
                <StatusBadge status={item.status} />
              )}
            </Box>
          </div>
        </div>

        <div className="approved-release-card__meta">
          <span className="approved-release-card__id">FM-LY-{item.lyrics_id}</span>
          <span className="approved-release-card__id">RL-{item.release_id}</span>
          <CopyMeta icon={<Music size={13} />} label="Audio" value={item.audio_title} />
        </div>
        <div className="approved-release-card__meta" style={{ marginTop: 6 }}>
          <CopyMeta icon={<User size={13} />} label="User" value={item.name} />
          <CopyMeta icon={<Mail size={13} />} label="Email" value={item.email} />
          {item.date && (
            <CopyMeta icon={<Calendar size={13} />} label="Date" value={item.date} />
          )}
        </div>
      </div>
    </Paper>
  );

  return (
    <Grid container>
      {/* ========== Review Required ========== */}
      <Grid item xs={12}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={2}>
          <Typography variant="h6" sx={{ color: '#F1F5F9', fontWeight: 600, fontSize: '1rem' }}>
            Review Required
          </Typography>
          <Chip
            icon={<FileText size={14} />}
            label={`${lyricsNew.length} Pending`}
            sx={{
              backgroundColor: 'rgba(245, 158, 11, 0.12)',
              color: '#F59E0B',
              border: '1px solid rgba(245, 158, 11, 0.25)',
              fontWeight: 600,
              fontSize: '0.85rem',
              height: 34,
              flexShrink: 0,
              '& .MuiChip-icon': { color: '#F59E0B' },
            }}
          />
        </Box>
        <Box className="approved-release-list" mb={4}>
          {lyricsNew.length ? (
            lyricsNew.map((item) => renderCard(item, true))
          ) : (
            <Paper sx={{ p: 0 }}>
              <table style={{ width: '100%' }}><tbody>
                <EmptyState message="No Lyrics To Review" colSpan={1} />
              </tbody></table>
            </Paper>
          )}
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Divider sx={{ borderColor: 'var(--border)', mb: 3 }} />
      </Grid>

      {/* ========== History ========== */}
      <Grid item xs={12}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={2}>
          <Typography variant="h6" sx={{ color: '#F1F5F9', fontWeight: 600, fontSize: '1rem' }}>
            Lyrics History
          </Typography>
          <Chip
            icon={<FileText size={14} />}
            label={`${count} Total`}
            sx={{
              backgroundColor: 'rgba(108, 99, 255, 0.12)',
              color: '#6C63FF',
              border: '1px solid rgba(108, 99, 255, 0.25)',
              fontWeight: 600,
              fontSize: '0.85rem',
              height: 34,
              flexShrink: 0,
              '& .MuiChip-icon': { color: '#6C63FF' },
            }}
          />
        </Box>
        <Box mb={3} sx={{ maxWidth: 420 }}>
          <SearchInput
            onChange={onChangeSearch}
            value={filters.search}
            placeholder="Search by name, email, or release..."
          />
        </Box>
        <Box className="approved-release-list">
          {lyricsOld.length ? (
            lyricsOld.map((item) => renderCard(item, false))
          ) : (
            <Paper sx={{ p: 0 }}>
              <table style={{ width: '100%' }}><tbody>
                <EmptyState message="No Lyrics History Found" colSpan={1} />
              </tbody></table>
            </Paper>
          )}
        </Box>

        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination
              count={totalPages}
              showFirstButton
              showLastButton
              onChange={onPageChange}
              page={filters.page || 1}
              sx={{
                '& .MuiPaginationItem-root': {
                  color: '#94A3B8',
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(108, 99, 255, 0.2)',
                    color: '#6C63FF',
                  },
                },
              }}
            />
          </Box>
        )}
      </Grid>

      {/* ========== Action Modal ========== */}
      <CustomShowModal
        handleClose={() => setActionsModal(false)}
        title="What's your action?"
        open={actionsModal}
        maxWidth="sm"
      >
        <Box display="flex" justifyContent="center" gap={2} py={2}>
          <ActionButton variant="success" onClick={() => onConfirmAcceptModal("approved")}>
            Accept
          </ActionButton>
          <ActionButton variant="danger" onClick={() => onConfirmAcceptModal("rejected")}>
            Reject
          </ActionButton>
        </Box>
      </CustomShowModal>

      {/* ========== Details Modal ========== */}
      <CustomShowModal
        open={viewDetailsModal}
        handleClose={() => setViewDetailsModal(false)}
        maxWidth="sm"
        title={detailsData?.release_title || "Lyrics Details"}
      >
        <Box py={1}>
          {detailsData?.p_rights && (
            <DetailField label="Publishing Rights" value={detailsData.p_rights} />
          )}
          {detailsData?.lyrics_language && (
            <DetailCopyRow icon={<Languages size={13} />} label="Language" value={detailsData.lyrics_language} />
          )}
          {detailsData?.iswc && (
            <DetailCopyRow icon={<Hash size={13} />} label="ISWC" value={detailsData.iswc} />
          )}
          {detailsData?.lrc_link && (
            <Box className="copyable-field" sx={{ py: 1 }}>
              <span className="copyable-field__label">LRC File</span>
              <Box display="flex" alignItems="center" gap={0.5} sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ color: '#F1F5F9', fontSize: '0.85rem' }}>Available</Typography>
                <CopyBtn value={detailsData.lrc_link} />
                <Tooltip title="Open" arrow placement="top">
                  <IconButton size="small" component="a" href={detailsData.lrc_link} target="_blank" rel="noreferrer" sx={{ color: 'var(--text-secondary)', '&:hover': { color: 'var(--primary)' } }}>
                    <ExternalLink size={14} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download" arrow placement="top">
                  <IconButton size="small" component="a" href={detailsData.lrc_link} download target="_blank" rel="noreferrer" sx={{ color: 'var(--text-secondary)', '&:hover': { color: 'var(--primary)' } }}>
                    <Download size={14} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          )}
          {detailsData?.lyrics && (
            <Box sx={{ py: 1 }}>
              <Typography sx={{ color: '#94A3B8', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
                Lyrics
              </Typography>
              <Paper sx={{
                p: 2,
                backgroundColor: 'rgba(15, 17, 23, 0.6)',
                border: '1px solid var(--border)',
                maxHeight: 300,
                overflowY: 'auto',
                position: 'relative',
                '&::-webkit-scrollbar': { width: '4px' },
                '&::-webkit-scrollbar-thumb': { background: '#2E3247', borderRadius: '4px' },
              }}>
                <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                  <CopyBtn value={detailsData.lyrics} />
                </Box>
                <Typography sx={{ color: '#F1F5F9', fontSize: '0.85rem', whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                  {detailsData.lyrics}
                </Typography>
              </Paper>
            </Box>
          )}
        </Box>
      </CustomShowModal>
    </Grid>
  );
}

function CopyBtn({ value }) {
  const [copied, setCopied] = useState(false);
  return (
    <Tooltip title={copied ? "Copied!" : "Copy"} arrow placement="top">
      <IconButton
        size="small"
        onClick={() => { copyToClipboard(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
        sx={{ color: copied ? '#10B981' : 'var(--text-secondary)', '&:hover': { color: 'var(--primary)' } }}
      >
        {copied ? <ClipboardCheck size={14} /> : <Copy size={14} />}
      </IconButton>
    </Tooltip>
  );
}

function DetailField({ label, value }) {
  const [copied, setCopied] = useState(false);
  return (
    <Box className="copyable-field" sx={{ py: 1, flexDirection: 'column', alignItems: 'flex-start' }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
        <Typography sx={{ color: '#94A3B8', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
          {label}
        </Typography>
        <CopyBtn value={value} />
      </Box>
      <Typography sx={{ color: '#F1F5F9', fontSize: '0.85rem', wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Box>
  );
}

function DetailCopyRow({ icon, label, value }) {
  const [copied, setCopied] = useState(false);
  return (
    <Box className="copyable-field" sx={{ py: 1 }}>
      <span className="copyable-field__label">
        {icon && <span style={{ marginRight: 4, display: 'inline-flex' }}>{icon}</span>}
        {label}
      </span>
      <span className="copyable-field__value">{value}</span>
      <CopyBtn value={value} />
    </Box>
  );
}
